import React, { useEffect, useState } from "react";
import "../styles/shared-ui.css";
import "../styles/user-management.css";
import "../styles/audit-logs.css";
import TopTabs from "../components/layout/TopTabs";
import {
  getLoginLogs,
  getActionLogs,
  getFailedLogins,
  getDeletions,
} from "../services/auditService";
import { toast } from "../utils/toast";
import { useAppContext } from "../context/AppContext";
import { resolveRoleName } from "../utils/auth";

const AuditLogsPage = () => {
  const { setActiveSection } = useAppContext();
  const [roleChecked, setRoleChecked] = useState(false);
  const [canAccess, setCanAccess] = useState(false);
  const [activeTab, setActiveTab] = useState("logins");
  const [loading, setLoading] = useState(false);

  // Login logs state
  const [loginLogs, setLoginLogs] = useState([]);
  const [loginFilters, setLoginFilters] = useState({
    user_id: "",
    email: "",
    success: "",
    from_date: "",
    to_date: "",
  });
  const [loginPage, setLoginPage] = useState(1);
  const [loginLimit, setLoginLimit] = useState(25);
  // eslint-disable-next-line no-unused-vars
  const [loginTotal, setLoginTotal] = useState(0);
  const [loginTotalPages, setLoginTotalPages] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let user = null;
        try {
          const raw = sessionStorage.getItem("auth");
          const auth = raw ? JSON.parse(raw) : null;
          user = auth?.user || null;
        } catch {}
        const rn = user ? await resolveRoleName(user) : null;
        const norm = String(rn || "").toUpperCase();
        const allowed = norm === "SUPER_ADMIN" || norm === "SUPERADMIN";
        if (!cancelled) {
          setCanAccess(allowed);
          setRoleChecked(true);
        }
        if (!allowed) setActiveSection("dashboard");
      } catch {
        if (!cancelled) {
          setCanAccess(false);
          setRoleChecked(true);
        }
        setActiveSection("dashboard");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setActiveSection]);

  // Action logs state
  const [actionLogs, setActionLogs] = useState([]);
  const [actionFilters, setActionFilters] = useState({
    user_id: "",
    entity_type: "",
    entity_id: "",
    action: "",
    from_date: "",
    to_date: "",
  });
  const [actionPage, setActionPage] = useState(1);
  const [actionLimit, setActionLimit] = useState(25);
  // eslint-disable-next-line no-unused-vars
  const [actionTotal, setActionTotal] = useState(0);
  const [actionTotalPages, setActionTotalPages] = useState(0);

  // Failed logins state
  const [failedLogins, setFailedLogins] = useState([]);
  const [failedLoginsHours, setFailedLoginsHours] = useState(24);
  const [failedLoginsPage, setFailedLoginsPage] = useState(1);
  const [failedLoginsLimit, setFailedLoginsLimit] = useState(25);
  // eslint-disable-next-line no-unused-vars
  const [failedLoginsTotal, setFailedLoginsTotal] = useState(0);
  const [failedLoginsTotalPages, setFailedLoginsTotalPages] = useState(0);

  // Deletions state
  const [deletions, setDeletions] = useState([]);
  const [deletionsHours, setDeletionsHours] = useState(24);
  const [deletionsPage, setDeletionsPage] = useState(1);
  const [deletionsLimit, setDeletionsLimit] = useState(25);
  // eslint-disable-next-line no-unused-vars
  const [deletionsTotal, setDeletionsTotal] = useState(0);
  const [deletionsTotalPages, setDeletionsTotalPages] = useState(0);

  // Load data when tab changes or when page/limit changes for active tab
  useEffect(() => {
    setLoading(true);
    loadTabData().finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, loginPage, loginLimit, actionPage, actionLimit, failedLoginsPage, failedLoginsLimit, deletionsPage, deletionsLimit]);

  const loadTabData = async () => {
    try {
      switch (activeTab) {
        case "logins":
          await loadLoginLogs();
          break;
        case "actions":
          await loadActionLogs();
          break;
        case "failedLogins":
          await loadFailedLogins();
          break;
        case "deletions":
          await loadDeletions();
          break;
        default:
          break;
      }
    } catch (error) {
      toast.error(error?.message || "Failed to load audit logs");
    }
  };

  const loadLoginLogs = async () => {
    try {
      const params = {
        ...loginFilters,
        page: loginPage,
        limit: loginLimit,
      };
      // Remove empty filters
      Object.keys(params).forEach(
        (key) =>
          (params[key] === "" || params[key] === null) && delete params[key],
      );

      const response = await getLoginLogs(params);
      setLoginLogs(response?.data || []);
      setLoginTotal(response?.meta?.total || 0);
      setLoginTotalPages(response?.meta?.totalPages || 0);
    } catch (error) {
      toast.error(error?.message || "Failed to load login logs");
      setLoginLogs([]);
    }
  };

  const loadActionLogs = async () => {
    try {
      const params = {
        ...actionFilters,
        page: actionPage,
        limit: actionLimit,
      };
      // Remove empty filters
      Object.keys(params).forEach(
        (key) =>
          (params[key] === "" || params[key] === null) && delete params[key],
      );

      const response = await getActionLogs(params);
      setActionLogs(response?.data || []);
      setActionTotal(response?.meta?.total || 0);
      setActionTotalPages(response?.meta?.totalPages || 0);
    } catch (error) {
      toast.error(error?.message || "Failed to load action logs");
      setActionLogs([]);
    }
  };

  const loadFailedLogins = async () => {
    try {
      const params = {
        hours: failedLoginsHours || 24,
        page: failedLoginsPage,
        limit: failedLoginsLimit,
      };

      const response = await getFailedLogins(params);
      setFailedLogins(response?.data || []);
      setFailedLoginsTotal(response?.meta?.total || 0);
      setFailedLoginsTotalPages(response?.meta?.totalPages || 0);
    } catch (error) {
      toast.error(error?.message || "Failed to load failed logins");
      setFailedLogins([]);
    }
  };

  const loadDeletions = async () => {
    try {
      const params = {
        hours: deletionsHours || 24,
        page: deletionsPage,
        limit: deletionsLimit,
      };

      const response = await getDeletions(params);
      setDeletions(response?.data || []);
      setDeletionsTotal(response?.meta?.total || 0);
      setDeletionsTotalPages(response?.meta?.totalPages || 0);
    } catch (error) {
      toast.error(error?.message || "Failed to load deletions");
      setDeletions([]);
    }
  };

  const handleApplyLoginFilters = () => {
    setLoginPage(1);
    loadLoginLogs();
  };

  const handleClearLoginFilters = () => {
    setLoginFilters({
      user_id: "",
      email: "",
      success: "",
      from_date: "",
      to_date: "",
    });
    setLoginPage(1);
    setTimeout(() => loadLoginLogs(), 0);
  };

  const handleApplyActionFilters = () => {
    setActionPage(1);
    loadActionLogs();
  };

  const handleClearActionFilters = () => {
    setActionFilters({
      user_id: "",
      entity_type: "",
      entity_id: "",
      action: "",
      from_date: "",
      to_date: "",
    });
    setActionPage(1);
    setTimeout(() => loadActionLogs(), 0);
  };

  const handleApplyFailedLoginsFilter = () => {
    setFailedLoginsPage(1);
    loadFailedLogins();
  };

  const handleApplyDeletionsFilter = () => {
    setDeletionsPage(1);
    loadDeletions();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  };

  // eslint-disable-next-line no-unused-vars
  const Pagination = ({ page, totalPages, onPageChange, total, limit }) => {
    const startItem = (page - 1) * limit + 1;
    const endItem = Math.min(page * limit, total);

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "1rem",
          padding: "1rem",
          borderTop: "1px solid var(--gray-200)",
        }}
      >
        <div style={{ fontSize: "0.875rem", color: "var(--gray-500)" }}>
          Showing {total > 0 ? startItem : 0} to {endItem} of {total} results
        </div>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <button
            className="secondary-btn sm"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            style={{ opacity: page <= 1 ? 0.5 : 1 }}
          >
            Previous
          </button>
          <span style={{ fontSize: "0.875rem", padding: "0 0.5rem" }}>
            Page {page} of {totalPages || 1}
          </span>
          <button
            className="secondary-btn sm"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            style={{ opacity: page >= totalPages ? 0.5 : 1 }}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  const renderLoginLogsTab = () => (
    <div className="role-card">
      {/* Enhanced Filters Section */}
      <div className="audit-filter-section">
        <div className="audit-filter-header">
          <h3 className="audit-filter-title">Advanced Filters</h3>
          <span className="audit-filter-badge">{loginLogs.length} Records</span>
        </div>
        <div className="audit-filter-grid">
          <div className="audit-filter-group">
            <label className="audit-filter-label">User ID</label>
            <input
              type="number"
              className="audit-filter-input"
              value={loginFilters.user_id}
              onChange={(e) =>
                setLoginFilters({ ...loginFilters, user_id: e.target.value })
              }
              placeholder="Enter user ID"
            />
          </div>
          <div className="audit-filter-group">
            <label className="audit-filter-label">Email Address</label>
            <input
              type="text"
              className="audit-filter-input"
              value={loginFilters.email}
              onChange={(e) =>
                setLoginFilters({ ...loginFilters, email: e.target.value })
              }
              placeholder="Search by email"
            />
          </div>
          <div className="audit-filter-group">
            <label className="audit-filter-label">Login Status</label>
            <select
              className="audit-filter-select"
              value={loginFilters.success}
              onChange={(e) =>
                setLoginFilters({ ...loginFilters, success: e.target.value })
              }
            >
              <option value="">All Statuses</option>
              <option value="true">✓ Successful</option>
              <option value="false">✗ Failed</option>
            </select>
          </div>
          <div className="audit-filter-group">
            <label className="audit-filter-label">From Date</label>
            <input
              type="datetime-local"
              className="audit-filter-input"
              value={loginFilters.from_date}
              onChange={(e) =>
                setLoginFilters({ ...loginFilters, from_date: e.target.value })
              }
            />
          </div>
          <div className="audit-filter-group">
            <label className="audit-filter-label">To Date</label>
            <input
              type="datetime-local"
              className="audit-filter-input"
              value={loginFilters.to_date}
              onChange={(e) =>
                setLoginFilters({ ...loginFilters, to_date: e.target.value })
              }
            />
          </div>
        </div>
        <div className="audit-filter-actions">
          <button
            className="audit-filter-btn audit-filter-btn-apply"
            onClick={handleApplyLoginFilters}
          >
            Apply Filters
          </button>
          <button
            className="audit-filter-btn audit-filter-btn-clear"
            onClick={handleClearLoginFilters}
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Enhanced Table */}
      {loading ?
        <div className="audit-loading">Loading login logs...</div>
      : <>
          <div className="audit-table-container">
            <div
              className="audit-table-header"
              style={{
                display: "grid",
                gridTemplateColumns: "80px 200px 150px 120px 180px 1fr",
              }}
            >
              <div className="cell">User ID</div>
              <div className="cell">Email</div>
              <div className="cell">IP Address</div>
              <div className="cell">Status</div>
              <div className="cell">Timestamp</div>
              <div className="cell">User Agent</div>
            </div>

            {loginLogs.length === 0 ?
              <div className="audit-empty">
                <div className="audit-empty-title">No Login Logs Found</div>
                <div className="audit-empty-text">
                  Try adjusting your filters to see more results
                </div>
              </div>
            : loginLogs.map((log, idx) => (
                <div
                  key={idx}
                  className="audit-table-row"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "80px 200px 150px 120px 180px 1fr",
                  }}
                >
                  <div className="cell">{log.user_id || "-"}</div>
                  <div
                    className="cell"
                    title={log.email}
                    style={{ fontWeight: 500 }}
                  >
                    {log.email || "-"}
                  </div>
                  <div
                    className="cell"
                    style={{
                      fontFamily: "monospace",
                      fontSize: "0.8rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    {log.ip_address && log.ip_address !== "-" ?
                      <>
                        <a
                          href={`https://whatismyipaddress.com/ip/${log.ip_address}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="View IP details"
                          style={{
                            color: "var(--accent-500)",
                            textDecoration: "none",
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{ flexShrink: 0 }}
                          >
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                            <polyline points="15 3 21 3 21 9"></polyline>
                            <line x1="10" y1="14" x2="21" y2="3"></line>
                          </svg>
                        </a>
                        <span>{log.ip_address}</span>
                      </>
                    : "-"}
                  </div>
                  <div className="cell">
                    <span
                      className={`audit-status-badge ${
                        log.success ?
                          "audit-status-success"
                        : "audit-status-failed"
                      }`}
                    >
                      {log.success ? "Success" : "Failed"}
                    </span>
                  </div>
                  <div className="cell" style={{ fontSize: "0.8rem" }}>
                    {formatDate(log.created_at || log.timestamp)}
                  </div>
                  <div
                    className="cell"
                    title={log.user_agent}
                    style={{
                      fontSize: "0.75rem",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {log.user_agent || "-"}
                  </div>
                </div>
              ))
            }
          </div>
          <div className="audit-pagination">
            <div className="audit-pagination-info">
              <label style={{ marginRight: "0.5rem", fontWeight: 500 }}>
                Items per page:
              </label>
              <select
                className="audit-filter-select"
                style={{
                  padding: "0.5rem",
                  borderRadius: "6px",
                  border: "2px solid var(--gray-200)",
                  background: "var(--color-surface)",
                  fontSize: "0.875rem",
                  cursor: "pointer",
                }}
                value={loginLimit}
                onChange={(e) => {
                  setLoginLimit(Number(e.target.value));
                  setLoginPage(1);
                }}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
            <div className="audit-pagination-controls">
              <button
                className="audit-pagination-btn"
                onClick={() => setLoginPage(loginPage - 1)}
                disabled={loginPage <= 1}
              >
                ← Previous
              </button>
              <span className="audit-pagination-current">
                Page {loginPage} of {loginTotalPages || 1}
              </span>
              <button
                className="audit-pagination-btn"
                onClick={() => setLoginPage(loginPage + 1)}
                disabled={loginPage >= loginTotalPages}
              >
                Next →
              </button>
            </div>
          </div>
        </>
      }
    </div>
  );

  const renderActionLogsTab = () => (
    <div className="role-card">
      {/* Enhanced Filters Section */}
      <div className="audit-filter-section">
        <div className="audit-filter-header">
          <h3 className="audit-filter-title">Action Filters</h3>
          <span className="audit-filter-badge">
            {actionLogs.length} Actions
          </span>
        </div>
        <div className="audit-filter-grid">
          <div className="audit-filter-group">
            <label className="audit-filter-label">User ID</label>
            <input
              type="number"
              className="audit-filter-input"
              value={actionFilters.user_id}
              onChange={(e) =>
                setActionFilters({ ...actionFilters, user_id: e.target.value })
              }
              placeholder="Filter by user"
            />
          </div>
          <div className="audit-filter-group">
            <label className="audit-filter-label">Entity Type</label>
            <input
              type="text"
              className="audit-filter-input"
              value={actionFilters.entity_type}
              onChange={(e) =>
                setActionFilters({
                  ...actionFilters,
                  entity_type: e.target.value,
                })
              }
              placeholder="company, building, etc."
            />
          </div>
          <div className="audit-filter-group">
            <label className="audit-filter-label">Entity ID</label>
            <input
              type="text"
              className="audit-filter-input"
              value={actionFilters.entity_id}
              onChange={(e) =>
                setActionFilters({
                  ...actionFilters,
                  entity_id: e.target.value,
                })
              }
              placeholder="Specific entity ID"
            />
          </div>
          <div className="audit-filter-group">
            <label className="audit-filter-label">Action Type</label>
            <select
              className="audit-filter-select"
              value={actionFilters.action}
              onChange={(e) =>
                setActionFilters({ ...actionFilters, action: e.target.value })
              }
            >
              <option value="">All Actions</option>
              <option value="CREATE">➕ CREATE</option>
              <option value="UPDATE">✏️ UPDATE</option>
              <option value="DELETE">🗑️ DELETE</option>
            </select>
          </div>
          <div className="audit-filter-group">
            <label className="audit-filter-label">From Date</label>
            <input
              type="datetime-local"
              className="audit-filter-input"
              value={actionFilters.from_date}
              onChange={(e) =>
                setActionFilters({
                  ...actionFilters,
                  from_date: e.target.value,
                })
              }
            />
          </div>
          <div className="audit-filter-group">
            <label className="audit-filter-label">To Date</label>
            <input
              type="datetime-local"
              className="audit-filter-input"
              value={actionFilters.to_date}
              onChange={(e) =>
                setActionFilters({ ...actionFilters, to_date: e.target.value })
              }
            />
          </div>
        </div>
        <div className="audit-filter-actions">
          <button
            className="audit-filter-btn audit-filter-btn-apply"
            onClick={handleApplyActionFilters}
          >
            Apply Filters
          </button>
          <button
            className="audit-filter-btn audit-filter-btn-clear"
            onClick={handleClearActionFilters}
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Enhanced Table */}
      {loading ?
        <div className="audit-loading">Loading action logs...</div>
      : <>
          <div className="audit-table-container">
            <div
              className="audit-table-header"
              style={{
                display: "grid",
                gridTemplateColumns: "80px 120px 150px 120px 180px 180px",
              }}
            >
              <div className="cell">User ID</div>
              <div className="cell">Action</div>
              <div className="cell">Entity Type</div>
              <div className="cell">Entity ID</div>
              <div className="cell">IP Address</div>
              <div className="cell">Timestamp</div>
            </div>

            {actionLogs.length === 0 ?
              <div className="audit-empty">
                <div className="audit-empty-title">No Action Logs Found</div>
                <div className="audit-empty-text">
                  Try adjusting your filters to see more results
                </div>
              </div>
            : actionLogs.map((log, idx) => (
                <div
                  key={idx}
                  className="audit-table-row"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "80px 120px 150px 120px 180px 180px",
                  }}
                >
                  <div className="cell">{log.user_id || "-"}</div>
                  <div className="cell">
                    <span
                      className={`audit-action-badge ${
                        log.action === "CREATE" ? "audit-action-create"
                        : log.action === "UPDATE" ? "audit-action-update"
                        : "audit-action-delete"
                      }`}
                    >
                      {log.action}
                    </span>
                  </div>
                  <div className="cell" style={{ fontWeight: 500 }}>
                    {log.entity_type || "-"}
                  </div>
                  <div className="cell" style={{ fontFamily: "monospace" }}>
                    {log.entity_id || "-"}
                  </div>
                  <div
                    className="cell"
                    style={{
                      fontSize: "0.8rem",
                      fontFamily: "monospace",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    {log.ip_address && log.ip_address !== "-" ?
                      <>
                        <a
                          href={`https://whatismyipaddress.com/ip/${log.ip_address}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="View IP details"
                          style={{
                            color: "var(--accent-500)",
                            textDecoration: "none",
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{ flexShrink: 0 }}
                          >
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                            <polyline points="15 3 21 3 21 9"></polyline>
                            <line x1="10" y1="14" x2="21" y2="3"></line>
                          </svg>
                        </a>
                        <span>{log.ip_address}</span>
                      </>
                    : "-"}
                  </div>
                  <div className="cell" style={{ fontSize: "0.8rem" }}>
                    {formatDate(log.created_at || log.timestamp)}
                  </div>
                </div>
              ))
            }
          </div>
          <div className="audit-pagination">
            <div className="audit-pagination-info">
              <label style={{ marginRight: "0.5rem", fontWeight: 500 }}>
                Items per page:
              </label>
              <select
                className="audit-filter-select"
                style={{
                  padding: "0.5rem",
                  borderRadius: "6px",
                  border: "2px solid var(--gray-200)",
                  background: "var(--color-surface)",
                  fontSize: "0.875rem",
                  cursor: "pointer",
                }}
                value={actionLimit}
                onChange={(e) => {
                  setActionLimit(Number(e.target.value));
                  setActionPage(1);
                }}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
            <div className="audit-pagination-controls">
              <button
                className="audit-pagination-btn"
                onClick={() => setActionPage(actionPage - 1)}
                disabled={actionPage <= 1}
              >
                ← Previous
              </button>
              <span className="audit-pagination-current">
                Page {actionPage} of {actionTotalPages || 1}
              </span>
              <button
                className="audit-pagination-btn"
                onClick={() => setActionPage(actionPage + 1)}
                disabled={actionPage >= actionTotalPages}
              >
                Next →
              </button>
            </div>
          </div>
        </>
      }
    </div>
  );

  const renderFailedLoginsTab = () => (
    <div className="role-card">
      {/* Enhanced Filter Section */}
      <div className="audit-filter-section">
        <div className="audit-filter-header">
          <h3 className="audit-filter-title">Time Range Filter</h3>
          <span className="audit-filter-badge">
            {failedLogins.length} Failed Attempts
          </span>
        </div>
        <div className="audit-time-range" style={{ marginBottom: "1rem" }}>
          <button
            className={`audit-time-btn ${
              failedLoginsHours === 1 ? "active" : ""
            }`}
            onClick={() => {
              setFailedLoginsHours(1);
              setFailedLoginsPage(1);
            }}
          >
            Last Hour
          </button>
          <button
            className={`audit-time-btn ${
              failedLoginsHours === 24 ? "active" : ""
            }`}
            onClick={() => {
              setFailedLoginsHours(24);
              setFailedLoginsPage(1);
            }}
          >
            Last 24 Hours
          </button>
          <button
            className={`audit-time-btn ${
              failedLoginsHours === 72 ? "active" : ""
            }`}
            onClick={() => {
              setFailedLoginsHours(72);
              setFailedLoginsPage(1);
            }}
          >
            Last 3 Days
          </button>
          <button
            className={`audit-time-btn ${
              failedLoginsHours === 168 ? "active" : ""
            }`}
            onClick={() => {
              setFailedLoginsHours(168);
              setFailedLoginsPage(1);
            }}
          >
            Last Week
          </button>
        </div>
        <div
          className="audit-filter-grid"
          style={{ gridTemplateColumns: "1fr 1fr" }}
        >
          <div className="audit-filter-group">
            <label className="audit-filter-label">Custom Hours Back</label>
            <input
              type="number"
              className="audit-filter-input"
              value={failedLoginsHours}
              onChange={(e) => setFailedLoginsHours(e.target.value)}
              placeholder="Enter custom hours"
              min="1"
            />
          </div>
        </div>
        <div className="audit-filter-actions">
          <button
            className="audit-filter-btn audit-filter-btn-apply"
            onClick={handleApplyFailedLoginsFilter}
          >
            Apply Filter
          </button>
        </div>
      </div>

      {/* Enhanced Table */}
      {loading ?
        <div className="audit-loading">Loading failed login attempts...</div>
      : <>
          <div className="audit-table-container">
            <div
              className="audit-table-header"
              style={{
                display: "grid",
                gridTemplateColumns: "80px 200px 180px 180px 1fr",
              }}
            >
              <div className="cell">User ID</div>
              <div className="cell">Email</div>
              <div className="cell">IP Address</div>
              <div className="cell">Timestamp</div>
              <div className="cell">Reason</div>
            </div>

            {failedLogins.length === 0 ?
              <div className="audit-empty">
                <div className="audit-empty-title">
                  No Failed Login Attempts
                </div>
                <div className="audit-empty-text">
                  Great! No failed login attempts in the selected timeframe
                </div>
              </div>
            : failedLogins.map((log, idx) => (
                <div
                  key={idx}
                  className="audit-table-row"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "80px 200px 180px 180px 1fr",
                  }}
                >
                  <div className="cell">{log.user_id || "-"}</div>
                  <div className="cell" style={{ fontWeight: 500 }}>
                    {log.email || "-"}
                  </div>
                  <div
                    className="cell"
                    style={{
                      fontFamily: "monospace",
                      fontSize: "0.8rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    {log.ip_address && log.ip_address !== "-" ?
                      <>
                        <a
                          href={`https://whatismyipaddress.com/ip/${log.ip_address}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="View IP details"
                          style={{
                            color: "var(--accent-500)",
                            textDecoration: "none",
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{ flexShrink: 0 }}
                          >
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                            <polyline points="15 3 21 3 21 9"></polyline>
                            <line x1="10" y1="14" x2="21" y2="3"></line>
                          </svg>
                        </a>
                        <span>{log.ip_address}</span>
                      </>
                    : "-"}
                  </div>
                  <div className="cell" style={{ fontSize: "0.8rem" }}>
                    {formatDate(log.created_at || log.timestamp)}
                  </div>
                  <div
                    className="cell"
                    style={{ fontSize: "0.8rem", color: "var(--red-ink-800)" }}
                  >
                    {log.reason || log.error_message || "Invalid credentials"}
                  </div>
                </div>
              ))
            }
          </div>
          <div className="audit-pagination">
            <div className="audit-pagination-info">
              <label style={{ marginRight: "0.5rem", fontWeight: 500 }}>
                Items per page:
              </label>
              <select
                className="audit-filter-select"
                style={{
                  padding: "0.5rem",
                  borderRadius: "6px",
                  border: "2px solid var(--gray-200)",
                  background: "var(--color-surface)",
                  fontSize: "0.875rem",
                  cursor: "pointer",
                }}
                value={failedLoginsLimit}
                onChange={(e) => {
                  setFailedLoginsLimit(Number(e.target.value));
                  setFailedLoginsPage(1);
                }}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
            <div className="audit-pagination-controls">
              <button
                className="audit-pagination-btn"
                onClick={() => setFailedLoginsPage(failedLoginsPage - 1)}
                disabled={failedLoginsPage <= 1}
              >
                ← Previous
              </button>
              <span className="audit-pagination-current">
                Page {failedLoginsPage} of {failedLoginsTotalPages || 1}
              </span>
              <button
                className="audit-pagination-btn"
                onClick={() => setFailedLoginsPage(failedLoginsPage + 1)}
                disabled={failedLoginsPage >= failedLoginsTotalPages}
              >
                Next →
              </button>
            </div>
          </div>
        </>
      }
    </div>
  );

  const renderDeletionsTab = () => (
    <div className="role-card">
      {/* Enhanced Filter Section */}
      <div className="audit-filter-section">
        <div className="audit-filter-header">
          <h3 className="audit-filter-title">Deletion History Filter</h3>
          <span className="audit-filter-badge">
            {deletions.length} Deletions
          </span>
        </div>
        <div className="audit-time-range" style={{ marginBottom: "1rem" }}>
          <button
            className={`audit-time-btn ${deletionsHours === 1 ? "active" : ""}`}
            onClick={() => {
              setDeletionsHours(1);
              setDeletionsPage(1);
            }}
          >
            Last Hour
          </button>
          <button
            className={`audit-time-btn ${
              deletionsHours === 24 ? "active" : ""
            }`}
            onClick={() => {
              setDeletionsHours(24);
              setDeletionsPage(1);
            }}
          >
            Last 24 Hours
          </button>
          <button
            className={`audit-time-btn ${
              deletionsHours === 72 ? "active" : ""
            }`}
            onClick={() => {
              setDeletionsHours(72);
              setDeletionsPage(1);
            }}
          >
            Last 3 Days
          </button>
          <button
            className={`audit-time-btn ${
              deletionsHours === 168 ? "active" : ""
            }`}
            onClick={() => {
              setDeletionsHours(168);
              setDeletionsPage(1);
            }}
          >
            Last Week
          </button>
        </div>
        <div
          className="audit-filter-grid"
          style={{ gridTemplateColumns: "1fr 1fr" }}
        >
          <div className="audit-filter-group">
            <label className="audit-filter-label">Custom Hours Back</label>
            <input
              type="number"
              className="audit-filter-input"
              value={deletionsHours}
              onChange={(e) => setDeletionsHours(e.target.value)}
              placeholder="Enter custom hours"
              min="1"
            />
          </div>
        </div>
        <div className="audit-filter-actions">
          <button
            className="audit-filter-btn audit-filter-btn-apply"
            onClick={handleApplyDeletionsFilter}
          >
            Apply Filter
          </button>
        </div>
      </div>

      {/* Enhanced Table */}
      {loading ?
        <div className="audit-loading">Loading deletion records...</div>
      : <>
          <div className="audit-table-container">
            <div
              className="audit-table-header"
              style={{
                display: "grid",
                gridTemplateColumns: "80px 150px 120px 180px 180px",
              }}
            >
              <div className="cell">User ID</div>
              <div className="cell">Entity Type</div>
              <div className="cell">Entity ID</div>
              <div className="cell">IP Address</div>
              <div className="cell">Timestamp</div>
            </div>

            {deletions.length === 0 ?
              <div className="audit-empty">
                <div className="audit-empty-title">No Deletions Found</div>
                <div className="audit-empty-text">
                  No deletion records in the selected timeframe
                </div>
              </div>
            : deletions.map((log, idx) => (
                <div
                  key={idx}
                  className="audit-table-row"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "80px 150px 120px 180px 180px",
                  }}
                >
                  <div className="cell">{log.user_id || "-"}</div>
                  <div className="cell" style={{ fontWeight: 500 }}>
                    {log.entity_type || "-"}
                  </div>
                  <div className="cell" style={{ fontFamily: "monospace" }}>
                    {log.entity_id || "-"}
                  </div>
                  <div
                    className="cell"
                    style={{
                      fontSize: "0.8rem",
                      fontFamily: "monospace",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    {log.ip_address && log.ip_address !== "-" ?
                      <>
                        <a
                          href={`https://whatismyipaddress.com/ip/${log.ip_address}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="View IP details"
                          style={{
                            color: "var(--accent-500)",
                            textDecoration: "none",
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{ flexShrink: 0 }}
                          >
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                            <polyline points="15 3 21 3 21 9"></polyline>
                            <line x1="10" y1="14" x2="21" y2="3"></line>
                          </svg>
                        </a>
                        <span>{log.ip_address}</span>
                      </>
                    : "-"}
                  </div>
                  <div className="cell" style={{ fontSize: "0.8rem" }}>
                    {formatDate(log.created_at || log.timestamp)}
                  </div>
                </div>
              ))
            }
          </div>
          <div className="audit-pagination">
            <div className="audit-pagination-info">
              <label style={{ marginRight: "0.5rem", fontWeight: 500 }}>
                Items per page:
              </label>
              <select
                className="audit-filter-select"
                style={{
                  padding: "0.5rem",
                  borderRadius: "6px",
                  border: "2px solid var(--gray-200)",
                  background: "var(--color-surface)",
                  fontSize: "0.875rem",
                  cursor: "pointer",
                }}
                value={deletionsLimit}
                onChange={(e) => {
                  setDeletionsLimit(Number(e.target.value));
                  setDeletionsPage(1);
                }}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
            <div className="audit-pagination-controls">
              <button
                className="audit-pagination-btn"
                onClick={() => setDeletionsPage(deletionsPage - 1)}
                disabled={deletionsPage <= 1}
              >
                ← Previous
              </button>
              <span className="audit-pagination-current">
                Page {deletionsPage} of {deletionsTotalPages || 1}
              </span>
              <button
                className="audit-pagination-btn"
                onClick={() => setDeletionsPage(deletionsPage + 1)}
                disabled={deletionsPage >= deletionsTotalPages}
              >
                Next →
              </button>
            </div>
          </div>
        </>
      }
    </div>
  );

  if (!roleChecked) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="no-results">Loading...</div>
        </div>
      </div>
    );
  }
  if (!canAccess) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="no-results">Access denied</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">Audit Logs</h2>
            <p className="page-subtitle">
              View and monitor system activity and security events
            </p>
          </div>
        </div>
      </div>

      <div className="page-content">
        <TopTabs
          tabs={[
            { id: "logins", label: "Login Logs" },
            { id: "actions", label: "Action Logs" },
            { id: "failedLogins", label: "Failed Logins" },
            { id: "deletions", label: "Deletions" },
          ]}
          activeTab={activeTab}
          onTabChange={(tabId) => {
            setActiveTab(tabId);
            // Reset pagination when switching tabs
            if (tabId === "logins") setLoginPage(1);
            if (tabId === "actions") setActionPage(1);
            if (tabId === "failedLogins") setFailedLoginsPage(1);
            if (tabId === "deletions") setDeletionsPage(1);
          }}
        />

        {activeTab === "logins" && renderLoginLogsTab()}
        {activeTab === "actions" && renderActionLogsTab()}
        {activeTab === "failedLogins" && renderFailedLoginsTab()}
        {activeTab === "deletions" && renderDeletionsTab()}
      </div>
    </div>
  );
};

export default AuditLogsPage;
